/** Public GA4 configuration. Only the production Hub can load Google's tag. */
export const HUB_MEASUREMENT_ID = "G-FV45JGENYD";
export const HUB_ORIGIN = "https://hub.wirralmusicfactory.com";
export const CONSENT_KEY = "wmf:usage-consent:v2";
export const CONSENT_CHANGED = "wmf:usage-consent-changed";
export const CONSENT_DAYS = 180;
export const TESTING_EXCLUSION_KEY = "wmf:usage-testing-excluded:v1";
export const TESTING_EXCLUSION_CHANGED = "wmf:usage-testing-exclusion-changed";
const COOKIE_PREFIX = "wmf_hub";
const LIFETIME = CONSENT_DAYS * 86400 * 1000;
export type Audience = "staff" | "family";
export type AnalyticsAccount = { id: string; audience: Audience; excluded?: boolean };
export type Consent = { choice: "accepted" | "declined"; expiresAt: number };
const unsavedDeclines = new Map<string, Consent>();
let unsavedTestingExclusion = false;

export function readTestingExclusion(): boolean {
  if (unsavedTestingExclusion) return true;
  try { return window.localStorage.getItem(TESTING_EXCLUSION_KEY) === "true"; }
  catch { return true; }
}

export function saveTestingExclusion(excluded: boolean): boolean {
  // Excluding stops immediately. A failed attempt to include this browser
  // also stays closed, without interrupting an already-included browser when
  // its unchanged preference saves successfully.
  unsavedTestingExclusion = true;
  if (excluded) usageAnalytics.stop(true);
  try {
    if (excluded) window.localStorage.setItem(TESTING_EXCLUSION_KEY, "true");
    else window.localStorage.removeItem(TESTING_EXCLUSION_KEY);
    unsavedTestingExclusion = false;
    window.dispatchEvent(new Event(TESTING_EXCLUSION_CHANGED));
    return true;
  } catch {
    usageAnalytics.stop(true);
    window.dispatchEvent(new Event(TESTING_EXCLUSION_CHANGED));
    return false;
  }
}

// Account identifiers stay in this browser's preference storage and client
// state. They are never passed to Google's tag or included in event parameters.
// The old browser-wide v1 choice cannot grant consent to any account.
export function consentKey(accountId: string): string {
  return `${CONSENT_KEY}:${encodeURIComponent(accountId)}`;
}

export function readConsent(accountId: string | null): Consent | null {
  if (!accountId) return null;
  const unsaved = unsavedDeclines.get(accountId);
  if (unsaved && unsaved.expiresAt > Date.now()) return unsaved;
  unsavedDeclines.delete(accountId);
  try {
    const value = JSON.parse(window.localStorage.getItem(consentKey(accountId)) ?? "null") as Consent | null;
    return value && ["accepted", "declined"].includes(value.choice) &&
      Number.isFinite(value.expiresAt) && value.expiresAt > Date.now() &&
      value.expiresAt <= Date.now() + LIFETIME ? value : null;
  } catch { return null; }
}

export function saveConsent(accountId: string, choice: Consent["choice"]): boolean {
  if (!accountId) return false;
  try {
    window.localStorage.setItem(consentKey(accountId), JSON.stringify({ choice, expiresAt: Date.now() + LIFETIME }));
    unsavedDeclines.delete(accountId);
    window.dispatchEvent(new Event(CONSENT_CHANGED));
    return true;
  } catch {
    // A failed save must not leave an older acceptance active in this tab.
    unsavedDeclines.set(accountId, { choice: "declined", expiresAt: Date.now() + LIFETIME });
    window.dispatchEvent(new Event(CONSENT_CHANGED));
    return false;
  }
}

// Fixed names only: no actual URL, query, fragment, record ID or page title
// crosses this boundary. Unknown and authentication routes are excluded.
const PAGES: Record<string, string> = {
  home: "Home", today: "Today", diary: "Diary", lessons: "Lessons",
  history: "Lesson history", notes: "Lesson notes", practice: "Practice",
  metronome: "Metronome", progress: "Progress", awards: "Awards",
  book: "Book a lesson", students: "Students", repertoire: "Repertoire",
  files: "Files", grooves: "Grooves", "neck-diagrams": "Neck diagrams",
  notifications: "Notifications", inbox: "Inbox", availability: "Availability",
  "time-off": "Time off", "recurring-lessons": "Recurring lessons",
  waiting: "Action Centre", payments: "Payments", invoices: "Invoices",
  "my-teaching-income": "Teaching income", reports: "Reports", tutors: "Tutors",
  schools: "Schools", "school-pupils": "School pupils", "school-grooves": "School grooves",
  "school-payers": "School payers", "school-invoices": "School invoices",
  "school-credits": "School credits", invoicing: "Invoicing", staff: "Staff",
  settings: "Settings", accessibility: "Accessibility", account: "Your account",
  setup: "Family setup", calendar: "Calendar", bookings: "Lesson workspace",
};
export function safePage(pathname: string): { path: string; title: string } | null {
  const section = pathname.split("/")[1] ?? "";
  if (!Object.hasOwn(PAGES, section)) return null;
  return { path: `/${section}`, title: PAGES[section]! };
}

type AnalyticsWindow = Window & {
  wmfHubDataLayer?: unknown[];
} & Partial<Record<`ga-disable-${typeof HUB_MEASUREMENT_ID}`, boolean>>;
type Page = { path: string; title: string; audience: Audience; visit: string };

/** All public operations fail closed; Analytics must never break the Hub. */
export class UsageAnalytics {
  private accountId: string | null = null;
  private current: Page | null = null;
  private lastVisit: string | null = null;
  private loaded = false;
  private failed = false;
  private script: HTMLScriptElement | null = null;
  private command: ((...args: unknown[]) => void) | null = null;
  private configured = false;
  private active = false;

  constructor(readonly enabled: boolean) {}

  private permitted(): boolean {
    return this.enabled && this.active && this.current !== null && !readTestingExclusion() && readConsent(this.accountId)?.choice === "accepted";
  }

  sync(pathname: string, account: AnalyticsAccount | null): void {
    try {
      const accountId = account?.id || null;
      if (this.accountId !== accountId) this.stop();
      this.accountId = accountId;
      const page = safePage(pathname);
      const excluded = Boolean(account?.excluded || readTestingExclusion());
      this.current = !excluded && page && accountId && account ? { ...page, audience: account.audience, visit: `${accountId}:${pathname}` } : null;
      const consent = readConsent(accountId);
      this.active = Boolean(this.current && this.enabled && consent?.choice === "accepted");
      // Unknown auth state pauses without deleting a returning browser cookie.
      // Owner/test exclusions and a resolved account without consent clear the
      // Hub cookies. Exclusion flags and account IDs never enter tag parameters.
      if (!this.permitted()) { this.stop(excluded || (accountId !== null && consent?.choice !== "accepted")); return; }
      (window as AnalyticsWindow)[`ga-disable-${HUB_MEASUREMENT_ID}`] = false;
      if (!this.script && !this.failed) this.load();
      if (this.loaded) this.pageView();
    } catch { this.stop(); }
  }

  private load(): void {
    const win = window as AnalyticsWindow;
    const layer: unknown[] = [];
    win.wmfHubDataLayer = layer;
    this.command = function (..._args: unknown[]) { layer.push(arguments); };
    this.command("consent", "default", {
      analytics_storage: "granted", ad_storage: "denied",
      ad_user_data: "denied", ad_personalization: "denied",
    });
    this.command("set", this.parameters());
    this.command("js", new Date());
    // No config or events are queued while the script is downloading. If
    // consent is withdrawn, its eventual load has nothing to collect.
    const script = document.createElement("script");
    script.async = true;
    script.referrerPolicy = "no-referrer";
    script.src = `https://www.googletagmanager.com/gtag/js?id=${HUB_MEASUREMENT_ID}&l=wmfHubDataLayer`;
    script.onload = () => { this.loaded = true; if (this.permitted()) this.pageView(); };
    script.onerror = () => {
      // A blocker or network failure gets one attempt per full page load.
      // Route changes and consent toggles must not repeatedly inject the tag.
      this.failed = true;
      script.remove(); this.script = null; this.loaded = false;
    };
    this.script = script;
    document.head.append(script);
  }

  private parameters(): Record<string, string> {
    const page = this.current!;
    return {
      page_location: `${HUB_ORIGIN}${page.path}`,
      page_title: `WMF Hub – ${page.title}`,
      page_referrer: "", audience_type: page.audience,
    };
  }

  private pageView(): void {
    if (!this.permitted() || !this.command || !this.loaded) return;
    const parameters = this.parameters();
    this.command("set", parameters);
    this.command("config", HUB_MEASUREMENT_ID, {
      ...parameters, send_page_view: false,
      allow_google_signals: false, allow_ad_personalization_signals: false,
      cookie_domain: "none", cookie_prefix: COOKIE_PREFIX,
      cookie_expires: CONSENT_DAYS * 86400, cookie_update: false,
      cookie_flags: "SameSite=Lax;Secure",
      ...(this.configured ? { update: true } : {}),
    });
    this.configured = true;
    if (this.lastVisit === this.current!.visit) return;
    this.lastVisit = this.current!.visit;
    this.command("event", "page_view", { ...parameters, send_to: HUB_MEASUREMENT_ID });
  }

  event(name: "lesson_note_view" | "practice_resource_open"): void {
    try {
      if (!this.permitted() || !this.loaded || !this.command || !this.configured) return;
      // A closed vocabulary also protects callers compiled without TypeScript.
      if (!["lesson_note_view", "practice_resource_open"].includes(name)) return;
      this.command("event", name, { ...this.parameters(), send_to: HUB_MEASUREMENT_ID });
    } catch { /* Optional measurement cannot interrupt a lesson. */ }
  }

  stop(clearCookies = false): void {
    this.active = false;
    try {
      (window as AnalyticsWindow)[`ga-disable-${HUB_MEASUREMENT_ID}`] = true;
      this.lastVisit = null;
      // Auth bootstrap, sign-out and unmount pause collection without resetting
      // an accepted browser identity. Otherwise every reload looks like a new
      // visitor. Only withdrawal or missing/expired consent clears cookies.
      if (!clearCookies) return;
      // Keep essential login cookies and the main website's GA cookies intact.
      for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0]?.trim() ?? "";
        if (name.startsWith(`${COOKIE_PREFIX}_`)) {
          document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
        }
      }
    } catch { /* Storage can be blocked in private browsing. */ }
  }
}

export const usageAnalytics = new UsageAnalytics(
  import.meta.env.PROD && typeof window !== "undefined" && window.location.origin === HUB_ORIGIN,
);

export function trackUsage(name: "lesson_note_view" | "practice_resource_open"): void {
  usageAnalytics.event(name);
}
